import { useMemo } from 'react'
import { useLibraryMovies, useLibrarySeries } from './useLibrary'
import type { LibraryMovieEntry, LibrarySeriesEntry } from './useLibrary'

export interface ContinueWatchingMovie {
  kind: 'movie'
  tmdbId: number
  entry: LibraryMovieEntry
  lastWatchedMs: number
}

export interface ContinueWatchingSeries {
  kind: 'tv'
  tmdbId: number
  entry: LibrarySeriesEntry
  lastWatchedMs: number
  /**
   * Furthest-watched episode for the series — surfaces a "next up" target
   * for the UI without requiring a TMDB hit. The desktop has more elaborate
   * logic in SeriesService.getContinueWatchingEpisodes (advances to S+1
   * after a watched finale, etc.); this PWA version is the minimum.
   */
  furthestSeason: number | null
  furthestEpisode: number | null
}

export type ContinueWatchingItem = ContinueWatchingMovie | ContinueWatchingSeries

/**
 * Surfaces "in progress" library items so the library tab can show a
 * Continue Watching row at the top. Movies count as in-progress when
 * `record.progress > 0` and not yet seen; series count when any episode
 * has progress > 0 OR isWatched (so the row can suggest the next-up
 * episode after the user finishes the previous one).
 *
 * All sorting + filtering happens against the cloud doc fields, no TMDB
 * calls.  Sorted by last activity desc.
 */
export function useContinueWatchingMovies(limit = 20): ContinueWatchingMovie[] {
  const { entries } = useLibraryMovies()

  return useMemo(() => {
    const out: ContinueWatchingMovie[] = []
    for (const entry of entries) {
      const r = entry.record
      const progress = typeof r.progress === 'number' ? r.progress : 0
      if (progress <= 0) continue
      const lastWatched = r.lastWatched ? Date.parse(r.lastWatched) : 0
      out.push({
        kind: 'movie',
        tmdbId: entry.tmdbId,
        entry,
        lastWatchedMs: isNaN(lastWatched) ? 0 : lastWatched,
      })
    }
    out.sort((a, b) => b.lastWatchedMs - a.lastWatchedMs)
    return out.slice(0, limit)
  }, [entries, limit])
}

/**
 * Merged movies+series continue watching, sorted by last activity desc.
 * Used by the Home landing so we render a single row instead of two
 * separate ones.
 */
export function useContinueWatchingAll(limit = 20): ContinueWatchingItem[] {
  const movies = useContinueWatchingMovies(limit)
  const series = useContinueWatchingSeries(limit)
  return useMemo(() => {
    const merged: ContinueWatchingItem[] = [...movies, ...series]
    merged.sort((a, b) => b.lastWatchedMs - a.lastWatchedMs)
    return merged.slice(0, limit)
  }, [movies, series, limit])
}

export function useContinueWatchingSeries(limit = 20): ContinueWatchingSeries[] {
  const { entries } = useLibrarySeries()

  return useMemo(() => {
    const out: ContinueWatchingSeries[] = []
    for (const entry of entries) {
      const eps = entry.record.episodes
      if (!eps) continue

      let lastWatchedMs = 0
      let furthestKey = -1 // season * 10000 + episode
      let furthestSeason: number | null = null
      let furthestEpisode: number | null = null
      let anyActivity = false

      for (const [seasonStr, season] of Object.entries(eps)) {
        if (!season) continue
        const sn = parseInt(seasonStr, 10)
        if (isNaN(sn)) continue
        for (const [epStr, ep] of Object.entries(season)) {
          if (!ep) continue
          const en = parseInt(epStr, 10)
          if (isNaN(en)) continue
          const progress = typeof ep.progress === 'number' ? ep.progress : 0
          const watched = !!ep.isWatched
          if (progress > 0 || watched) {
            anyActivity = true
            const ts = ep.lastWatched ? Date.parse(ep.lastWatched) : 0
            if (!isNaN(ts) && ts > lastWatchedMs) lastWatchedMs = ts
            const key = sn * 10000 + en
            if (key > furthestKey) {
              furthestKey = key
              furthestSeason = sn
              furthestEpisode = en
            }
          }
        }
      }

      if (!anyActivity) continue
      out.push({
        kind: 'tv',
        tmdbId: entry.tmdbId,
        entry,
        lastWatchedMs,
        furthestSeason,
        furthestEpisode,
      })
    }
    out.sort((a, b) => b.lastWatchedMs - a.lastWatchedMs)
    return out.slice(0, limit)
  }, [entries, limit])
}
